// Venue User Permissions System
// Defines permissions for different venue user roles

export const USER_ROLES = {
  VIEWER: 'viewer',
  MANAGER: 'manager',
  OWNER: 'venue_owner'
};

export const PERMISSIONS = {
  // Dashboard permissions
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_REPORTS: 'export_reports',
  
  // Venue permissions
  VIEW_VENUES: 'view_venues',
  ADD_VENUES: 'add_venues',
  EDIT_VENUES: 'edit_venues',
  DELETE_VENUES: 'delete_venues',
  MANAGE_VENUE_AVAILABILITY: 'manage_venue_availability',
  UPLOAD_VENUE_MEDIA: 'upload_venue_media',
  
  // Booking permissions
  VIEW_BOOKINGS: 'view_bookings',
  APPROVE_BOOKINGS: 'approve_bookings',
  REJECT_BOOKINGS: 'reject_bookings',
  MODIFY_BOOKINGS: 'modify_bookings',
  CANCEL_BOOKINGS: 'cancel_bookings',
  MANAGE_BOOKING_STATUS: 'manage_booking_status',
  PROCESS_PAYMENTS: 'process_payments',
  
  // Calendar permissions
  VIEW_CALENDAR: 'view_calendar',
  MANAGE_CALENDAR: 'manage_calendar',
  BLOCK_DATES: 'block_dates',
  SET_AVAILABILITY: 'set_availability',
  
  // Customer permissions
  VIEW_CUSTOMERS: 'view_customers',
  COMMUNICATE_WITH_CUSTOMERS: 'communicate_with_customers',
  
  // Reviews permissions
  VIEW_REVIEWS: 'view_reviews',
  RESPOND_TO_REVIEWS: 'respond_to_reviews',
  MODERATE_REVIEWS: 'moderate_reviews',
  
  // Local Attractions permissions
  VIEW_ATTRACTIONS: 'view_attractions',
  ADD_ATTRACTIONS: 'add_attractions',
  EDIT_ATTRACTIONS: 'edit_attractions',
  DELETE_ATTRACTIONS: 'delete_attractions',
  
  // Chat permissions
  ACCESS_CHAT: 'access_chat',
  MANAGE_CONVERSATIONS: 'manage_conversations',
  
  // Notifications permissions
  VIEW_NOTIFICATIONS: 'view_notifications',
  MANAGE_NOTIFICATIONS: 'manage_notifications',
  
  // Profile permissions
  VIEW_PROFILE: 'view_profile',
  EDIT_PROFILE: 'edit_profile',
  EDIT_BUSINESS_SETTINGS: 'edit_business_settings',
  
  // Financial permissions
  VIEW_REVENUE: 'view_revenue',
  MANAGE_PRICING: 'manage_pricing',
  VIEW_FINANCIAL_REPORTS: 'view_financial_reports'
};

// Define permissions for each role
export const ROLE_PERMISSIONS = {
  [USER_ROLES.VIEWER]: [
    // View-only access for basic information
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_VENUES,
    PERMISSIONS.VIEW_BOOKINGS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.VIEW_ATTRACTIONS,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.VIEW_PROFILE
  ],
  
  [USER_ROLES.MANAGER]: [
    // Manager with operational control but limited financial access
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_VENUES,
    PERMISSIONS.EDIT_VENUES,
    PERMISSIONS.ADD_VENUES,
    PERMISSIONS.MANAGE_VENUE_AVAILABILITY,
    PERMISSIONS.UPLOAD_VENUE_MEDIA,
    PERMISSIONS.VIEW_BOOKINGS,
    PERMISSIONS.APPROVE_BOOKINGS,
    PERMISSIONS.REJECT_BOOKINGS,
    PERMISSIONS.MODIFY_BOOKINGS,
    PERMISSIONS.CANCEL_BOOKINGS,
    PERMISSIONS.MANAGE_BOOKING_STATUS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.MANAGE_CALENDAR,
    PERMISSIONS.BLOCK_DATES,
    PERMISSIONS.SET_AVAILABILITY,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.RESPOND_TO_REVIEWS,
    PERMISSIONS.VIEW_ATTRACTIONS,
    PERMISSIONS.ADD_ATTRACTIONS,
    PERMISSIONS.EDIT_ATTRACTIONS,
    PERMISSIONS.DELETE_ATTRACTIONS,
    PERMISSIONS.MANAGE_CONVERSATIONS,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE,
    PERMISSIONS.VIEW_REVENUE
  ],

  [USER_ROLES.OWNER]: [
    // Venue owners have full access to all features
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.VIEW_VENUES,
    PERMISSIONS.ADD_VENUES,
    PERMISSIONS.EDIT_VENUES,
    PERMISSIONS.DELETE_VENUES,
    PERMISSIONS.MANAGE_VENUE_AVAILABILITY,
    PERMISSIONS.UPLOAD_VENUE_MEDIA,
    PERMISSIONS.VIEW_BOOKINGS,
    PERMISSIONS.APPROVE_BOOKINGS,
    PERMISSIONS.REJECT_BOOKINGS,
    PERMISSIONS.MODIFY_BOOKINGS,
    PERMISSIONS.CANCEL_BOOKINGS,
    PERMISSIONS.MANAGE_BOOKING_STATUS,
    PERMISSIONS.PROCESS_PAYMENTS,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.MANAGE_CALENDAR,
    PERMISSIONS.BLOCK_DATES,
    PERMISSIONS.SET_AVAILABILITY,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.COMMUNICATE_WITH_CUSTOMERS,
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.RESPOND_TO_REVIEWS,
    PERMISSIONS.MODERATE_REVIEWS,
    PERMISSIONS.VIEW_ATTRACTIONS,
    PERMISSIONS.ADD_ATTRACTIONS,
    PERMISSIONS.EDIT_ATTRACTIONS,
    PERMISSIONS.DELETE_ATTRACTIONS,
    PERMISSIONS.ACCESS_CHAT,
    PERMISSIONS.MANAGE_CONVERSATIONS,
    PERMISSIONS.VIEW_NOTIFICATIONS,
    PERMISSIONS.MANAGE_NOTIFICATIONS,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE,
    PERMISSIONS.EDIT_BUSINESS_SETTINGS,
    PERMISSIONS.VIEW_REVENUE,
    PERMISSIONS.MANAGE_PRICING,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS
  ]
};

// Helper functions
export const getUserRole = () => {
  const userData = localStorage.getItem('user');
  if (userData) {
    const user = JSON.parse(userData);
    // Check for venue-specific role property or default to owner
    return user.organizer_access || "venue_owner";
  }
  return "venue_owner"; // Default to owner for venue system
};

export const hasPermission = (permission) => {
  const userRole = getUserRole();
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  return rolePermissions.includes(permission);
};

export const checkMultiplePermissions = (permissions) => {
  return permissions.every(permission => hasPermission(permission));
};

export const getUserPermissions = (role = null) => {
  const userRole = getUserRole(); 
  return ROLE_PERMISSIONS[userRole] || [];
};

export const getPermissionDisplayData = (t) => {
  const userRole = getUserRole();
  const userPermissions = getUserPermissions();
  
  return [
    {
      title: t('permissions.dashboard.title'),
      description: t('permissions.dashboard.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_DASHBOARD)
    },
    {
      title: t('permissions.venues.view.title'),
      description: t('permissions.venues.view.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_VENUES)
    },
    {
      title: t('permissions.venues.manage.title'),
      description: t('permissions.venues.manage.description'),
      isGranted: userPermissions.includes(PERMISSIONS.ADD_VENUES)
    },
    {
      title: t('permissions.bookings.view.title'),
      description: t('permissions.bookings.view.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_BOOKINGS)
    },
    {
      title: t('permissions.bookings.manage.title'),
      description: t('permissions.bookings.manage.description'),
      isGranted: userPermissions.includes(PERMISSIONS.APPROVE_BOOKINGS)
    },
    {
      title: t('permissions.calendar.title'),
      description: t('permissions.calendar.description'),
      isGranted: userPermissions.includes(PERMISSIONS.MANAGE_CALENDAR)
    },
    {
      title: t('permissions.customers.title'),
      description: t('permissions.customers.description'),
      isGranted: userPermissions.includes(PERMISSIONS.COMMUNICATE_WITH_CUSTOMERS)
    },
    {
      title: t('permissions.reviews.view.title'),
      description: t('permissions.reviews.view.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_REVIEWS)
    },
    {
      title: t('permissions.reviews.manage.title'),
      description: t('permissions.reviews.manage.description'),
      isGranted: userPermissions.includes(PERMISSIONS.RESPOND_TO_REVIEWS)
    },
    {
      title: t('permissions.attractions.title'),
      description: t('permissions.attractions.description'),
      isGranted: userPermissions.includes(PERMISSIONS.ADD_ATTRACTIONS)
    },
    {
      title: t('permissions.chat.title'),
      description: t('permissions.chat.description'),
      isGranted: userPermissions.includes(PERMISSIONS.ACCESS_CHAT)
    },
    {
      title: t('permissions.financial.title'),
      description: t('permissions.financial.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_REVENUE)
    }
  ];
};

export default {
  USER_ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  getUserRole,
  hasPermission,
  checkMultiplePermissions,
  getUserPermissions,
  getPermissionDisplayData
};
